'use client';
// Token Lab — the AMT-20 token platform: deploy, mint, burn and transfer tokens
// directly in AmanChain consensus (no smart contracts, no external VM).
// Tokens can carry a consensus-stored logo and can be launched as stable tokens
// (100% AMAN-collateralized, mint/redeem at the peg inside consensus).
import { useState } from 'react';
import {
  Gem, Plus, Flame, ArrowLeftRight, Coins, Users, Loader2, ExternalLink, Hexagon, Send,
  TrendingUp, BadgeCheck, Link2, ShieldCheck, ImagePlus, Lock, Unlock, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchJson, post, usePoll, fmtAmt, fmtInt, shortHash, timeAgo, SectionHeader, Mono, Spinner, EmptyState, AddressChip, TokenAvatar } from './shared';
import { useSession, ConnectPrompt } from './session';
import type { Net, TokensData, TokenDetailT } from './types';
import { isEssential } from '@/lib/chain/essentials';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface TokensResponse extends TokensData { error?: string }

/** Client-side logo processing: square-fit, resize to 256px, PNG data URL inside the consensus cap */
async function fileToLogoDataUrl(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('Please choose an image file');
  if (file.size > 8 * 1024 * 1024) throw new Error('Image file is too large (max 8 MB before processing)');
  const bitmap = await createImageBitmap(file);
  const max = 256;
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable in this browser');
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const url = canvas.toDataURL('image/png');
  if (url.length > 120_000) throw new Error('Image is too large after encoding — pick a simpler/smaller picture');
  return url;
}

export function TokenLab({ net }: { net: Net }) {
  const { session } = useSession();
  const { data, loading, refresh } = usePoll<TokensResponse>(async () => fetchJson(`/api/tokens?net=${net}`), 4000, [net]);
  const [detail, setDetail] = useState<string | null>(null);

  if (loading && !data) return <Spinner label="Loading token registry…" />;
  if (data && data.launched === false) return <EmptyState icon={Gem} title="Network not launched" desc="Launch the network first to deploy tokens." />;
  if (!data || data.error) return <EmptyState icon={Gem} title="Token registry unavailable" desc={data?.error} />;

  const stats = data.stats ?? { count: 0, holders: 0, supply: 0 };
  const fee = data.deployFee ?? 25;

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-2xl border border-violet-500/25 glass p-5 md:p-7">
        <div className="absolute inset-0 grid-bg" aria-hidden />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-500/10 ring-1 ring-violet-500/30 px-3 py-1 text-xs font-bold text-violet-300">
              <Hexagon className="size-3.5" /> {data.standard} — native token standard
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30 px-3 py-1 text-xs font-bold text-emerald-400">
              <ShieldCheck className="size-3.5" /> Every deployment burns {fee} AMAN
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-4">
            Build your own token <span className="grad-text">on AmanChain</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Deploy a token in seconds — like issuing on the major programmable networks, but with supply caps,
            minting and burning enforced by consensus itself. Tokens inherit the full quantum shield of the chain
            and their deployment fee is permanently burned in AMAN.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            {([
              ['Tokens deployed', fmtInt(stats.count), Coins],
              ['Total holders', fmtInt(stats.holders), Users],
              ['Aggregate supply', fmtAmt(stats.supply, 0), TrendingUp],
              ['Deployment fee', `${fee} AMAN`, Flame],
            ] as Array<[string, string, React.ComponentType<{ className?: string }>]>).map(([label, value, Icon]) => (
                <div key={label} className="rounded-xl bg-muted/40 ring-1 ring-border p-3.5">
                  <div className="flex items-center gap-1.5 text-[0.65rem] text-muted-foreground"><Icon className="size-3.5" /> {label}</div>
                  <div className="text-lg font-black mt-0.5" dir="ltr">{value}</div>
                </div>
              ))}
          </div>
        </div>
      </section>

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          {session
            ? <CreateToken net={net} fee={fee} onDone={refresh} />
            : <ConnectPrompt what="deploy tokens" />}
        </div>
        <div className="lg:col-span-3">
          <TokenRegistry tokens={data.tokens ?? []} onOpen={setDetail} />
        </div>
      </div>

      <TokenDetailDialog net={net} symbol={detail} onClose={() => setDetail(null)} onChanged={refresh} />
    </div>
  );
}

// ---------- Creation wizard ----------
function CreateToken({ net, fee, onDone }: {
  net: Net; fee: number; onDone: () => void;
}) {
  const { session, wallet, openAccount } = useSession();
  const [form, setForm] = useState({ name: '', symbol: '', decimals: '2', initial: '', max: '', description: '', website: '' });
  const [logo, setLogo] = useState<string | null>(null);
  const [stable, setStable] = useState(false);
  const [peg, setPeg] = useState('1');
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));
  const initial = stable ? 0 : Number(form.initial || 0);
  const max = Number(form.max || 0);
  const pegNum = Number(peg || 0);

  const pickLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setLogoBusy(true);
    try {
      setLogo(await fileToLogoDataUrl(file));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not process the image');
    } finally {
      setLogoBusy(false);
    }
  };

  const valid =
    form.name.trim().length >= 3 && form.name.trim().length <= 40 &&
    /^[A-Z0-9]{2,8}$/.test(form.symbol) && /[A-Z]/.test(form.symbol) &&
    Number.isInteger(initial) && initial >= 0 &&
    Number.isInteger(max) && max >= 1 && initial <= max &&
    Number.isInteger(Number(form.decimals)) && Number(form.decimals) >= 0 && Number(form.decimals) <= 8 &&
    (!stable || (pegNum > 0 && Number.isFinite(pegNum)));

  const submit = async () => {
    if (!session) { openAccount(); return; }
    setBusy(true);
    try {
      const res = await post<{ id?: string; error?: string; message?: string }>(`/api/tx?net=${net}`, {
        walletId: session.walletId, password: session.password, type: 'token_create',
        tokenName: form.name.trim(), symbol: form.symbol.toUpperCase().trim(),
        decimals: Number(form.decimals), initialSupply: initial, maxSupply: max,
        description: form.description.trim() || undefined, website: form.website.trim() || undefined,
        logo: logo ?? undefined,
        stable: stable || undefined,
        pegAman: stable ? pegNum : undefined,
      });
      if (res.error) { toast.error(res.error); return; }
      toast.success('Deployment queued', { description: `${form.symbol} will appear in the registry once the transaction is mined.` });
      setForm({ name: '', symbol: '', decimals: '2', initial: '', max: '', description: '', website: '' });
      setLogo(null); setStable(false); setPeg('1');
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="glass p-5">
      <SectionHeader icon={Plus} title="Deploy a new token" desc="The wizard signs an AMT-20 deployment from your wallet" />
      <div className="space-y-3.5">
        <div className="flex items-center gap-4">
          <div className="relative">
            {logo ? (
              <span className="relative inline-block">
                <TokenAvatar symbol={form.symbol || 'TOKEN'} logo={logo} size={56} />
                <button
                  onClick={() => setLogo(null)}
                  className="absolute -top-1.5 -right-1.5 rounded-full bg-rose-500 text-white p-0.5 hover:bg-rose-400"
                  title="Remove logo"
                >
                  <X className="size-3" />
                </button>
              </span>
            ) : (
              <span className="inline-flex items-center justify-center rounded-xl bg-violet-500/10 ring-1 ring-violet-500/30 text-violet-300" style={{ width: 56, height: 56 }}>
                {logoBusy ? <Loader2 className="size-5 animate-spin" /> : <ImagePlus className="size-5" />}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-logo">Token logo <span className="font-normal">(optional)</span></label>
            <Input id="tk-logo" type="file" accept="image/*" onChange={pickLogo} className="text-xs file:mr-2 file:rounded-md file:border-0 file:bg-muted file:px-2 file:py-1 file:text-xs" />
            <p className="text-[0.62rem] text-muted-foreground mt-1">Stored inside consensus — shown in wallets, DEX and explorer. Without one, the symbol tile is used.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-name">Token name</label>
            <Input id="tk-name" value={form.name} onChange={set('name')} placeholder="Nova Protocol Credit" className="text-sm" maxLength={40} />
          </div>
          <div>
            <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-sym">Symbol</label>
            <Input id="tk-sym" value={form.symbol} onChange={e => setForm(f => ({ ...f, symbol: e.target.value.toUpperCase() }))} placeholder="NOVA" className="text-sm mono" maxLength={8} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-dec">Decimals</label>
            <Input id="tk-dec" type="number" min={0} max={8} value={form.decimals} onChange={set('decimals')} className="text-sm" />
          </div>
          <div>
            <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-init">Initial supply</label>
            <Input id="tk-init" type="number" min={0} value={stable ? '0' : form.initial} onChange={set('initial')} disabled={stable} placeholder="400000" className="text-sm" />
          </div>
          <div>
            <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-max">Max supply</label>
            <Input id="tk-max" type="number" min={1} value={form.max} onChange={set('max')} placeholder="1000000" className="text-sm" />
          </div>
        </div>

        <label className="flex items-start gap-2.5 rounded-lg bg-amber-500/5 ring-1 ring-amber-500/25 px-3 py-2.5 cursor-pointer">
          <input type="checkbox" checked={stable} onChange={e => setStable(e.target.checked)} className="mt-0.5 accent-amber-500" />
          <span className="text-[0.7rem] leading-relaxed">
            <span className="font-bold text-amber-300">Stable token — fixed price like USDT</span>
            <span className="block text-muted-foreground">Every unit is minted only against locked AMAN collateral and is redeemable on-chain at the peg, so the market price stays pinned no matter the demand or supply.</span>
          </span>
        </label>
        {stable && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-peg">Peg — AMAN per 1 unit</label>
              <Input id="tk-peg" type="number" min={0} step="any" value={peg} onChange={e => setPeg(e.target.value)} className="text-sm" />
            </div>
            <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2 text-[0.68rem] text-muted-foreground self-end">
              Minting 1 unit locks <b className="text-foreground">{fmtAmt(pegNum || 0, 4)} AMAN</b> in the on-chain vault; redeeming returns it. Supply starts at zero.
            </div>
          </div>
        )}

        <div>
          <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-desc">Description <span className="font-normal">(optional)</span></label>
          <Input id="tk-desc" value={form.description} onChange={set('description')} placeholder="What does your token represent?" className="text-sm" maxLength={200} />
        </div>
        <div>
          <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-site">Website <span className="font-normal">(optional)</span></label>
          <Input id="tk-site" value={form.website} onChange={set('website')} placeholder="https://example.com" className="text-sm" maxLength={120} />
        </div>

        <div>
          <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="tk-wallet">Deploy from — your session wallet</label>
          <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2.5 text-sm font-bold truncate">
            {session ? `${session.name} · ${fmtAmt(wallet?.balance ?? 0, 2)} AMAN` : 'Not connected'}
          </div>
        </div>
        {session && (
          <div className="flex items-center gap-2 text-[0.7rem] text-muted-foreground">
            <Coins className="size-3.5 text-amber-400" />
            Deployment burns <b className="text-foreground">{fee} AMAN</b> — you hold {fmtAmt(wallet?.balance ?? 0, 2)} AMAN.
            {(wallet?.balance ?? 0) < fee && <span className="text-rose-400 font-bold">Insufficient AMAN for the fee.</span>}
          </div>
        )}
        <Button onClick={submit} disabled={!valid || busy || !session} className="w-full gap-2 font-black">
          {busy ? <Loader2 className="size-4 animate-spin" /> : !session ? <Lock className="size-4" /> : <Plus className="size-4" />}
          {!session ? 'Connect wallet to deploy' : busy ? 'Signing deployment…' : `Deploy token — burn ${fee} AMAN`}
        </Button>
        <p className="text-[0.65rem] text-muted-foreground leading-relaxed">
          {stable
            ? 'Stable mode: supply starts at zero — units are minted against AMAN collateral by anyone and redeemed at the peg, keeping the price pinned. The logo and all metadata live inside consensus.'
            : 'Initial supply is minted to your wallet at deployment. You can mint more later up to the max supply — only the creator can mint; anyone can burn their own holdings.'}
        </p>
      </div>
    </section>
  );
}

// ---------- Registry ----------
function TokenRegistry({ tokens, onOpen }: { tokens: TokensData['tokens']; onOpen: (symbol: string) => void }) {
  if (!tokens || tokens.length === 0) {
    return (
      <section className="glass p-5">
        <SectionHeader icon={Gem} title="Token registry" desc="Live deployments on this network" />
        <EmptyState icon={Gem} title="No tokens yet" desc="Deploy the first AMT-20 token of this network with the wizard." />
      </section>
    );
  }
  return (
    <section className="glass p-5">
      <SectionHeader icon={Gem} title="Token registry" desc={`${tokens.length} deployment${tokens.length === 1 ? '' : 's'} on this network — click a token for details`} />
      <div className="grid sm:grid-cols-2 gap-3 max-h-[640px] overflow-y-auto pl-1">
        {tokens.map(t => (
          <button key={t.symbol} onClick={() => onOpen(t.symbol)} className="glass glass-hover p-4 text-left">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <TokenAvatar symbol={t.symbol} logo={t.logo} size={40} />
                <div className="min-w-0">
                  <div className="font-bold text-sm truncate flex items-center gap-1.5">
                    {t.name}
                    {isEssential(t.symbol) && <span className="rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30 text-emerald-300 px-1.5 py-px text-[0.55rem] font-black">ESSENTIAL</span>}
                    {t.stable && <span className="rounded-full bg-amber-500/10 ring-1 ring-amber-500/30 text-amber-300 px-1.5 py-px text-[0.55rem] font-black">STABLE</span>}
                  </div>
                  <Mono className="text-[0.65rem]">{t.standard} · {t.symbol}{t.stable && t.pegAman ? ` · peg ${fmtAmt(t.pegAman, 4)} AMAN` : ''}</Mono>
                </div>
              </div>
              <span title="Deployed in consensus with the quantum shield" className="shrink-0 inline-flex">
                <BadgeCheck className="size-4 text-cyan-400" />
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div>
                <div className="text-[0.6rem] text-muted-foreground">Supply</div>
                <div className="text-xs font-black" dir="ltr">{fmtAmt(t.totalSupply, 0)}</div>
              </div>
              <div>
                <div className="text-[0.6rem] text-muted-foreground">Holders</div>
                <div className="text-xs font-black" dir="ltr">{fmtInt(t.holders)}</div>
              </div>
              <div>
                <div className="text-[0.6rem] text-muted-foreground">Burned</div>
                <div className="text-xs font-black text-orange-400" dir="ltr">{fmtAmt(t.burned, 0)}</div>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-[0.6rem] text-muted-foreground mb-1">
                <span>Minted of max supply</span>
                <span dir="ltr">{t.progressPct.toFixed(1)}%</span>
              </div>
              <Progress value={t.progressPct} className="h-1.5" />
            </div>
            <div className="flex items-center justify-between mt-3 text-[0.62rem] text-muted-foreground">
              <span>by <Mono className="text-[0.62rem]">{shortHash(t.creator, 6, 4)}</Mono></span>
              <span>{timeAgo(t.createdAt)}</span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ---------- Detail dialog with actions ----------
function TokenDetailDialog({ net, symbol, onClose, onChanged }: {
  net: Net; symbol: string | null; onClose: () => void; onChanged: () => void;
}) {
  const { data, refresh } = usePoll<{ launched: boolean; token: TokenDetailT } | null>(
    async () => (symbol ? fetchJson(`/api/tokens?net=${net}&symbol=${symbol}`) : Promise.resolve(null)),
    4000,
    [net, symbol],
  );
  const t = data?.token;

  return (
    <Dialog open={Boolean(symbol)} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            {t ? (
              <>
                <TokenAvatar symbol={t.symbol} logo={t.logo} size={40} />
                <span>{t.name}</span>
                <Mono className="text-xs">{t.symbol}</Mono>
                {t.stable && <span className="rounded-full bg-amber-500/10 ring-1 ring-amber-500/30 text-amber-300 px-2 py-0.5 text-[0.6rem] font-black">STABLE · 1 = {fmtAmt(t.pegAman ?? 1, 4)} AMAN</span>}
              </>
            ) : (
              <span>{symbol} — loading…</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-left">
            {t ? (
              <>
                {t.description || 'No description provided.'}
                {t.website && (
                  <a href={t.website.startsWith('http') ? t.website : `https://${t.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline ml-2">
                    <Link2 className="size-3" /> website <ExternalLink className="size-3" />
                  </a>
                )}
              </>
            ) : 'Fetching token details from the chain…'}
          </DialogDescription>
        </DialogHeader>

        {t ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              {[
                ['Total supply', fmtAmt(t.totalSupply, 0)],
                ['Max supply', fmtAmt(t.maxSupply, 0)],
                ['Burned', fmtAmt(t.burned, 0)],
                ['Holders', fmtInt(t.holders)],
              ].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-muted/40 ring-1 ring-border p-2.5 text-center">
                  <div className="text-[0.6rem] text-muted-foreground">{k}</div>
                  <div className="text-sm font-black mt-0.5" dir="ltr">{v}</div>
                </div>
              ))}
            </div>

            <div className="text-[0.68rem] text-muted-foreground">
              Creator: <AddressChip address={t.creator} /> · Deployed {timeAgo(t.createdAt)} · <Mono className="text-[0.62rem]">{shortHash(t.txId, 8, 6)}</Mono>
            </div>

            <Tabs defaultValue="holders" className="mt-1">
              <TabsList className="grid grid-cols-3 h-9">
                <TabsTrigger value="holders" className="text-xs">Holders</TabsTrigger>
                <TabsTrigger value="activity" className="text-xs">Activity</TabsTrigger>
                <TabsTrigger value="actions" className="text-xs">Actions</TabsTrigger>
              </TabsList>
              <TabsContent value="holders" className="mt-3">
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {t.topHolders.map((h, i) => (
                    <div key={h.address} className="flex items-center justify-between rounded-lg bg-muted/30 ring-1 ring-border/60 px-3 py-2">
                      <span className="text-xs flex items-center gap-2">
                        <span className="text-muted-foreground w-4">#{i + 1}</span>
                        <AddressChip address={h.address} />
                      </span>
                      <span className="text-xs font-black" dir="ltr">{fmtAmt(h.balance, 2)} {t.symbol}</span>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="activity" className="mt-3">
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {t.transfers.map(tr => (
                    <div key={tr.txId + tr.kind + tr.ts} className="flex items-center justify-between rounded-lg bg-muted/30 ring-1 ring-border/60 px-3 py-2 text-[0.7rem]">
                      <span className="inline-flex items-center gap-1.5">
                        <TypeIcon kind={tr.kind} />
                        <Mono>{shortHash(tr.from, 6, 4)}</Mono> → <Mono>{shortHash(tr.to, 6, 4)}</Mono>
                      </span>
                      <span className="font-bold" dir="ltr">{fmtAmt(tr.amount, 2)} {t.symbol}</span>
                    </div>
                  ))}
                  {t.transfers.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">No movements yet.</div>}
                </div>
              </TabsContent>
              <TabsContent value="actions" className="mt-3">
                <TokenActions net={net} token={t} onChanged={() => { refresh(); onChanged(); }} />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="py-10"><Spinner label="Loading token…" /></div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TypeIcon({ kind }: { kind: string }) {
  if (kind === 'token_mint' || kind === 'stable_mint') return <Coins className="size-3 text-emerald-400" />;
  if (kind === 'token_burn' || kind === 'stable_redeem') return <Flame className="size-3 text-orange-400" />;
  if (kind === 'token_create') return <Plus className="size-3 text-violet-400" />;
  return <ArrowLeftRight className="size-3 text-violet-300" />;
}

// ---------- Mint / transfer / burn actions (stable tokens get collateral mint/redeem) ----------
function TokenActions({ net, token, onChanged }: {
  net: Net; token: TokenDetailT; onChanged: () => void;
}) {
  const { session, wallet, openAccount } = useSession();
  const [amount, setAmount] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);

  const isCreator = session?.address === token.creator;
  const holding = wallet?.assets.find(a => a.symbol === token.symbol)?.balance ?? 0;
  const isStable = Boolean(token.stable);
  const peg = token.pegAman ?? 1;
  const amt = Number(amount || 0);
  const collateralCost = Math.round(amt * peg * 1e8) / 1e8; // AMAN locked when minting

  const run = async (type: string) => {
    if (!session) { openAccount(); return; }
    setBusy(true);
    try {
      const res = await post<{ id?: string; error?: string }>(`/api/tx?net=${net}`, {
        walletId: session.walletId, password: session.password, type,
        symbol: token.symbol,
        amount: Number(amount),
        to: type === 'token_transfer' ? to.trim() : undefined,
      });
      if (res.error) { toast.error(res.error); return; }
      toast.success(`${type.replace('token_', '').replace('stable_', 'stable ')} queued`, { description: `${fmtAmt(Number(amount), 2)} ${token.symbol} — waiting to be mined.` });
      setAmount(''); setTo('');
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (!session) {
    return (
      <div className="text-center py-6 space-y-2">
        <p className="text-sm text-muted-foreground">Connect your wallet to interact with {token.symbol}.</p>
        <Button onClick={openAccount} size="sm" variant="secondary" className="gap-1.5 text-xs">Connect wallet</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {isStable && (
        <div className="rounded-lg bg-amber-500/5 ring-1 ring-amber-500/25 px-3 py-2.5 text-[0.7rem] text-amber-200/90 space-y-1">
          <div className="flex items-center gap-1.5 font-bold text-amber-300"><Lock className="size-3.5" /> Fully-backed stable — vault holds {fmtAmt(token.collateral ?? 0, 4)} AMAN for {fmtAmt(token.totalSupply, 2)} {token.symbol}</div>
          <div className="text-muted-foreground">Mint locks {fmtAmt(peg, 4)} AMAN per unit in the on-chain vault · Redeem burns units and releases exactly {fmtAmt(peg, 4)} AMAN per unit. This peg arbitrage is what pins the market price.</div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block">Wallet — your session</label>
          <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2.5 text-sm font-bold truncate">
            {session?.name} · {fmtAmt(holding, 2)} {token.symbol}
          </div>
        </div>
        <div>
          <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="ta-amount">Amount</label>
          <Input id="ta-amount" type="number" min={0} value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" className="text-sm" />
        </div>
      </div>

      {!isStable && isCreator && (
        <div className="rounded-lg bg-emerald-500/5 ring-1 ring-emerald-500/25 px-3 py-2 text-[0.7rem] text-emerald-300 flex items-center gap-1.5">
          <BadgeCheck className="size-3.5" /> You are the creator — minting unlocked. Remaining capacity: {fmtAmt(token.maxSupply - token.totalSupply, 0)} {token.symbol}.
        </div>
      )}
      {!isStable && !isCreator && (
        <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2 text-[0.7rem] text-muted-foreground">
          Minting is reserved for the token creator. You can transfer or burn your own {token.symbol}.
        </div>
      )}
      {isStable && (
        <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2 text-[0.7rem] text-muted-foreground">
          At this amount: <b className="text-emerald-300">mint</b> locks {fmtAmt(collateralCost, 4)} AMAN · <b className="text-amber-300">redeem</b> releases {fmtAmt(Math.round(amt * peg * 1e8) / 1e8, 4)} AMAN — open to every wallet, no permission needed.
        </div>
      )}

      {isStable ? (
        <div className="grid grid-cols-3 gap-2">
          <Button onClick={() => run('stable_mint')} disabled={busy || amt <= 0}
            className="gap-1.5 font-bold text-xs" size="sm" variant="secondary">
            <Lock className="size-3.5 text-emerald-400" /> Mint
          </Button>
          <Button onClick={() => run('stable_redeem')} disabled={busy || amt <= 0 || amt > holding}
            className="gap-1.5 font-bold text-xs ring-1 ring-amber-500/30 text-amber-300" size="sm" variant="secondary">
            <Unlock className="size-3.5" /> Redeem
          </Button>
          <Button onClick={() => run('token_transfer')} disabled={busy || amt <= 0 || amt > holding || !/^AM[0-9a-f]{48}$/.test(to.trim())}
            className="gap-1.5 font-bold text-xs" size="sm">
            <ArrowLeftRight className="size-3.5" /> Transfer
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <Button onClick={() => run('token_mint')} disabled={busy || !isCreator || Number(amount) <= 0}
            className="gap-1.5 font-bold text-xs" size="sm" variant="secondary">
            <Coins className="size-3.5 text-emerald-400" /> Mint
          </Button>
          <Button onClick={() => run('token_burn')} disabled={busy || Number(amount) <= 0 || Number(amount) > holding}
            className="gap-1.5 font-bold text-xs ring-1 ring-orange-500/30 text-orange-300" size="sm" variant="secondary">
            <Flame className="size-3.5" /> Burn
          </Button>
          <Button onClick={() => run('token_transfer')} disabled={busy || Number(amount) <= 0 || Number(amount) > holding || !/^AM[0-9a-f]{48}$/.test(to.trim())}
            className="gap-1.5 font-bold text-xs" size="sm">
            <ArrowLeftRight className="size-3.5" /> Transfer
          </Button>
        </div>
      )}
      <div>
        <label className="text-[0.68rem] font-semibold text-muted-foreground mb-1 block" htmlFor="ta-to">Recipient address <span className="font-normal">(for transfers)</span></label>
        <Input id="ta-to" value={to} onChange={e => setTo(e.target.value)} placeholder="AM…" className="text-sm mono" />
      </div>
    </div>
  );
}
